import { type ErrorDataObject, type FpoErrorEntry, type IErrorResponse } from '@/types/errors';

/**
 * Runtime narrowing for coo-api's error envelope — TEAM-13882.
 *
 * `error_data` is a union in practice (an FPO error array, a flat object, or absent), so reading any
 * field off it needs a guard. These live here rather than in `@/types/errors` because that module is
 * pure type declarations, and rather than in `@/utils/fpoErrors` because narrowing the envelope is
 * not an FPO-copy concern: `@/utils/errors` and `redux/me/saga.ts` need `failures` and
 * `unavailable_items` without caring about the render rule at all.
 *
 * The layering these modules form:
 *
 * - `@/types/errors` — the envelope's **shape**
 * - `@/utils/errorEnvelope` — **narrowing** that shape (this file)
 * - `@/utils/fpoErrors` — the diner-copy **render rule**
 * - `@/utils/errors` — **predicates** over the raw body (delivery/restaurant unavailable, ...)
 * - `@/utils/global` — the **funnel** (`prepareErrorMessage`), which applies the render rule once
 */

/** The non-array half of the `error_data` union, or `null` when `error_data` is an array/absent. */
export function getErrorDataObject(error: IErrorResponse | null): ErrorDataObject | null {
  const data = error?.error_data;
  if (!data || Array.isArray(data)) {
    return null;
  }
  return data;
}

/** The array half of the `error_data` union, or `[]` when `error_data` is an object/absent. */
export function getFpoErrorEntries(error: IErrorResponse | null): FpoErrorEntry[] {
  const data = error?.error_data;
  return Array.isArray(data) ? data : [];
}
